/**
 * Pricing Plan — the layout presets, named once.
 *
 * The table picks the default for its plans and each plan may override it, so
 * both inspectors need the same list; render.php validates against the same
 * slugs. A preset is nothing but a class — the markup is identical under all
 * four — which is why switching one needs no migration.
 */
import { __ } from '@wordpress/i18n';

export const PLAN_LAYOUT_PRESETS = [
  'classic',
  'cta-first',
  'split-header',
  'offer',
  'centered',
];

export const DEFAULT_PLAN_LAYOUT = 'classic';

/**
 * Options for a SelectControl on the table, which sets the default for every
 * plan inside it.
 *
 * @return {Array} Select options.
 */
export const planLayoutOptions = () => [
  { label: __('Classic stack', 'brandy-blocks'), value: 'classic' },
  { label: __('CTA above features', 'brandy-blocks'), value: 'cta-first' },
  { label: __('Split header row', 'brandy-blocks'), value: 'split-header' },
  { label: __('Offer — chooser above the price', 'brandy-blocks'), value: 'offer' },
  { label: __('Centred hero price', 'brandy-blocks'), value: 'centered' },
];

/**
 * The same options for a single plan, with the inherit case in front.
 *
 * An empty value means "whatever the table says" — the common case, so it is
 * both the default and the first entry.
 *
 * @return {Array} Select options.
 */
export const planLayoutOverrideOptions = () => [
  { label: __('Same as the table', 'brandy-blocks'), value: '' },
  ...planLayoutOptions(),
];

/**
 * Resolve a plan's preset from its own override and the table's default.
 *
 * Mirrored by render.php; keep the two in step.
 *
 * @param {string} override    The plan's layoutPreset attribute.
 * @param {string} tableLayout The table's planLayout, via context.
 * @return {string} A slug that is always one of PLAN_LAYOUT_PRESETS.
 */
export function resolvePlanLayout(override, tableLayout) {
  const candidate = override || tableLayout || DEFAULT_PLAN_LAYOUT;
  return PLAN_LAYOUT_PRESETS.includes(candidate) ? candidate : DEFAULT_PLAN_LAYOUT;
}

/**
 * What each preset implies for the parts that carry their own alignment.
 *
 * The preset moves the card's own pieces with CSS, but the feature list and
 * the button are separate blocks with separate alignment controls — CSS cannot
 * reach into an author's choice there, and should not try. Switching to the
 * centred layout and finding the ticks still hard left is the gap this closes.
 *
 * Applied on a change of preset only, never on mount: see the effect in the
 * plan's Edit for why. The author stays free to realign afterwards, and the
 * next preset change seeds again from here.
 */
export const PLAN_LAYOUT_PART_ALIGNMENT = {
  classic: { list: 'left', button: 'left' },
  'cta-first': { list: 'left', button: 'left' },
  'split-header': { list: 'left', button: 'left' },
  offer: { list: 'left', button: 'left' },
  centered: { list: 'center', button: 'center' },
};

/**
 * The attribute updates a preset implies, grouped by the block they apply to.
 *
 * Returns client ids rather than applying anything, so the caller decides how
 * to dispatch — one batch, one undo step.
 *
 * @param {string} preset The resolved layout preset.
 * @param {Array}  blocks The plan's blocks, nested.
 * @return {{lists: Array, buttons: Array, alignment: Object}} Ids to update.
 */
export function collectPartAlignmentTargets(preset, blocks) {
  const alignment =
    PLAN_LAYOUT_PART_ALIGNMENT[preset] || PLAN_LAYOUT_PART_ALIGNMENT.classic;

  const lists = [];
  const buttons = [];

  const walk = (list) =>
    (list || []).forEach((block) => {
      if (block.name === 'brandy/icon-list') {
        lists.push(block.clientId);
      }
      if (block.name === 'brandy/buttons') {
        buttons.push(block.clientId);
      }
      walk(block.innerBlocks);
    });

  walk(blocks);

  return { lists, buttons, alignment };
}
